import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { SchemaGraph, SchemaNode } from './graphLoader';
import { DocumentInfo } from '../../services/docProcessingApi';
import { TableDetailDrawer } from './TableDetailDrawer';

// ── Types ────────────────────────────────────────────────────────────────────

type SortCol = 'document' | 'module' | 'dbo' | 'table' | 'field';
type SortDir = 'asc' | 'desc';

interface PanelRow {
  document: string;
  module: string;
  dbo: string;
  table: string;
  field: string;
  /** Fully-qualified `database.schema.table` name, used as the stable
   *  selection key and as the FQN to insert into a SQL editor. Empty
   *  for "document-only" or "module-only" rows that don't have a table. */
  fqn: string;
}

export interface SchemaNodeListPanelProps {
  open: boolean;
  onClose: () => void;
  graphData: SchemaGraph | null;
  documents: DocumentInfo[];
  /** When true, render without the right-rail position chrome — fill
   *  100% of the parent container instead. Used by SchemaBrowserModal. */
  embedded?: boolean;
  /** Optional row-selection mode for the SchemaBrowser flow. When
   *  `'rows'`, an extra checkbox column appears on the left and rows
   *  whose `fqn` is in `selectedFqns` are highlighted. */
  selectionMode?: 'none' | 'rows';
  /** Set of fqns currently selected. Owned by the caller. */
  selectedFqns?: Set<string>;
  /** Toggle a single FQN's selection. */
  onToggleSelection?: (fqn: string) => void;
  /** Extra footer slot for the caller to render action buttons in. */
  footerSlot?: React.ReactNode;
}

// ── Constants ────────────────────────────────────────────────────────────────

const ROW_HEIGHT  = 26;
const OVERSCAN    = 10; // extra rows above/below viewport to avoid flicker on fast scroll

const COLS: { key: SortCol; label: string; width: number | 'flex' }[] = [
  // Widths bumped (was 110/80/70/90/flex) so long table, module, and
  // document names render in full instead of truncating.
  { key: 'document', label: 'Document', width: 140 },
  { key: 'module',   label: 'Module',   width: 140 },
  { key: 'dbo',      label: 'Dbo',      width: 80  },
  { key: 'table',    label: 'Table',    width: 200 },
  { key: 'field',    label: 'Field',    width: 'flex' },
];

const COL_COLORS: Record<SortCol, string> = {
  document: '#6d28d9',
  module:   '#1d4ed8',
  dbo:      '#065f46',
  table:    '#0369a1',
  field:    '#374151',
};

const SORT_CHAIN: SortCol[] = ['document', 'module', 'dbo', 'table', 'field'];

const EMPTY_FILTERS: Record<SortCol, string> = {
  document: '', module: '', dbo: '', table: '', field: '',
};

const VPN_CLIENT_PREFIXES = ['TASLI']; // ESL provisioned 2026-07-14, LSC (SAIL) 2026-07-15, HSL 2026-07-24, SWIRE 2026-10-07

// ── Module-level style helpers (stable — never recreated on render) ───────────

const CELL_BASE: React.CSSProperties = {
  padding: '0 8px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  borderRight: '1px solid #f3f4f6',
  display: 'flex',
  alignItems: 'center',
  fontSize: 11,
};

function makeCellStyle(width: number | 'flex', color: string): React.CSSProperties {
  return width === 'flex'
    ? { ...CELL_BASE, flex: 1, minWidth: 0, color }
    : { ...CELL_BASE, width, minWidth: width, flexShrink: 0, color };
}

// Pre-computed once at module load — zero allocation on every render.
// Widths MUST come from COLS so body cells stay aligned with the header and
// filter rows (they were previously hardcoded to stale values, which made
// every column misalign with its header).
const CELL_STYLES = Object.fromEntries(
  COLS.map((c) => [c.key, makeCellStyle(c.width, COL_COLORS[c.key])]),
) as Record<SortCol, React.CSSProperties>;

function makeHdrStyle(width: number | 'flex'): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '6px 8px', fontSize: 10, fontWeight: 600, color: '#374151',
    cursor: 'pointer', userSelect: 'none', borderRight: '1px solid #e5e7eb',
    display: 'flex', alignItems: 'center',
  };
  return width === 'flex'
    ? { ...base, flex: 1, minWidth: 0 }
    : { ...base, width, minWidth: width, flexShrink: 0 };
}

function makeFilterCellStyle(width: number | 'flex'): React.CSSProperties {
  const base: React.CSSProperties = { padding: '3px 4px', borderRight: '1px solid #e5e7eb' };
  return width === 'flex'
    ? { ...base, flex: 1, minWidth: 0 }
    : { ...base, width, minWidth: width, flexShrink: 0 };
}

// Pre-computed header and filter cell styles
const HDR_STYLES    = Object.fromEntries(COLS.map((c) => [c.key, makeHdrStyle(c.width)]));
const FILTER_STYLES = Object.fromEntries(COLS.map((c) => [c.key, makeFilterCellStyle(c.width)]));

// ── Helpers ──────────────────────────────────────────────────────────────────

function buildRows(
  graphData: SchemaGraph | null,
  documents: DocumentInfo[],
  showFields: boolean,
): PanelRow[] {
  const rows: PanelRow[] = [];

  // A table can belong to MANY modules — index it under every one so
  // filtering by any of its modules finds it.
  const moduleTableMap = new Map<string, SchemaNode[]>();
  if (graphData) {
    for (const node of graphData.nodes) {
      if (node.type !== 'SQLTable') continue;
      const mods = node.modules?.length ? node.modules : node.module ? [node.module] : [];
      for (const mod of mods) {
        const bucket = moduleTableMap.get(mod) ?? [];
        bucket.push(node);
        moduleTableMap.set(mod, bucket);
      }
    }
  }

  const claimedModules = new Set<string>();

  for (const doc of documents) {
    const filename = doc.filename ?? '';
    const linkedModules = doc.linked_modules ?? [];

    if (linkedModules.length === 0) {
      rows.push({ document: filename, module: '', dbo: '', table: '', field: '', fqn: '' });
      continue;
    }

    for (const moduleName of linkedModules) {
      claimedModules.add(moduleName);
      const tables = moduleTableMap.get(moduleName) ?? [];

      if (tables.length === 0) {
        rows.push({ document: filename, module: moduleName, dbo: '', table: '', field: '', fqn: '' });
        continue;
      }

      for (const tbl of tables) {
        if (!tbl.name || tbl.name.startsWith('$')) continue; // skip malformed table nodes
        const dbo = tbl.schema ?? tbl.database ?? '';
        const fqn = [tbl.database, tbl.schema, tbl.name].filter(Boolean).join('.');
        if (!showFields || !tbl.columns || tbl.columns.length === 0) {
          rows.push({ document: filename, module: moduleName, dbo, table: tbl.name, field: '', fqn });
        } else {
          for (const col of tbl.columns) {
            if (!col.name || col.name.startsWith('$')) continue; // skip Neo4j edge-ID artefacts
            rows.push({ document: filename, module: moduleName, dbo, table: tbl.name, field: col.name, fqn });
          }
        }
      }
    }
  }

  // Orphan tables — not linked from any document (via ANY of their modules)
  if (graphData) {
    for (const node of graphData.nodes) {
      const nodeModules = node.modules?.length ? node.modules : node.module ? [node.module] : [];
      const claimed = nodeModules.some((m) => claimedModules.has(m));
      if (node.type === 'SQLTable' && !claimed) {
        if (!node.name || node.name.startsWith('$')) continue; // skip malformed table nodes
        const dbo = node.schema ?? node.database ?? '';
        const fqn = [node.database, node.schema, node.name].filter(Boolean).join('.');
        const moduleName = nodeModules.join(', ');
        if (!showFields || !node.columns || node.columns.length === 0) {
          rows.push({ document: '', module: moduleName, dbo, table: node.name, field: '', fqn });
        } else {
          for (const col of node.columns) {
            if (!col.name || col.name.startsWith('$')) continue; // skip Neo4j edge-ID artefacts
            rows.push({ document: '', module: moduleName, dbo, table: node.name, field: col.name, fqn });
          }
        }
      }
    }
  }

  return rows;
}

// Fast comparison — avoids localeCompare overhead (10–50× faster for large lists)
function fastCmp(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Column-filter matcher. Plain text = case-insensitive "contains".
 * Prefix with `=` for an exact match — `=booking` matches the table named
 * exactly "booking" without flooding the list with bookingCntrlrpt etc.
 */
function matchesFilter(haystack: string, needle: string): boolean {
  if (needle.startsWith('=')) {
    return haystack.toLowerCase() === needle.slice(1).trim().toLowerCase();
  }
  return haystack.toLowerCase().includes(needle.toLowerCase());
}

// ── Debounce hook ─────────────────────────────────────────────────────────────

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SortIcon({ col, sortCol, sortDir }: { col: SortCol; sortCol: SortCol; sortDir: SortDir }) {
  if (sortCol !== col) return <span style={{ opacity: 0.3, fontSize: 9, marginLeft: 2 }}>↕</span>;
  return <span style={{ fontSize: 9, marginLeft: 2, color: '#6366f1' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
}

// ── Main Component ────────────────────────────────────────────────────────────

export function SchemaNodeListPanel({
  open, onClose, graphData, documents,
  embedded = false,
  selectionMode = 'none',
  selectedFqns,
  onToggleSelection,
  footerSlot,
}: SchemaNodeListPanelProps) {
  const showCheckboxes = selectionMode === 'rows';
  const CHECKBOX_COL_WIDTH = 32;
  const [searchInput, setSearchInput]   = useState('');
  const [filterInputs, setFilterInputs] = useState<Record<SortCol, string>>(EMPTY_FILTERS);
  const [sortCol, setSortCol]           = useState<SortCol>('document');
  const [sortDir, setSortDir]           = useState<SortDir>('asc');
  const [copied, setCopied]             = useState(false);
  const [showFields, setShowFields]     = useState(false);
  const [scrollTop, setScrollTop]       = useState(0);
  const [listHeight, setListHeight]     = useState(400);
  const scrollRef                       = useRef<HTMLDivElement>(null);
  /** Table detail drawer — set to open it; fieldFilter pre-fills the field search. */
  const [detail, setDetail] = useState<{ node: SchemaNode; fieldFilter: string } | null>(null);

  // fqn → node lookup for opening the detail drawer from a row click.
  const nodeByFqn = useMemo(() => {
    const m = new Map<string, SchemaNode>();
    if (graphData) {
      for (const n of graphData.nodes) {
        if (n.type === 'SQLTable' && n.name && !n.name.startsWith('$')) {
          m.set([n.database, n.schema, n.name].filter(Boolean).join('.'), n);
        }
      }
    }
    return m;
  }, [graphData]);

  const openDetail = useCallback((fqn: string, fieldFilter = '') => {
    const node = nodeByFqn.get(fqn);
    if (node) setDetail({ node, fieldFilter });
  }, [nodeByFqn]);

  const jumpToTable = useCallback((tableName: string) => {
    if (!graphData) return;
    const node = graphData.nodes.find(
      (n) => n.type === 'SQLTable' && n.name.toLowerCase() === tableName.toLowerCase(),
    );
    if (node) setDetail({ node, fieldFilter: '' });
  }, [graphData]);

  // Debounced values — filter/sort only recompute 300ms after typing stops
  const search  = useDebounce(searchInput, 300);
  const filters = useDebounce(filterInputs, 300);

  // Measure available height for the virtual list
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setListHeight(entry.contentRect.height));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Reset scroll when rows change (new filter/sort result)
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setScrollTop(0);
  }, [search, filters, sortCol, sortDir, showFields]);

  const allRows = useMemo(
    () => buildRows(graphData, documents, showFields),
    [graphData, documents, showFields],
  );

  const visibleRows = useMemo(() => {
    let rows = allRows;

    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter((r) =>
        r.document.toLowerCase().includes(q) || r.module.toLowerCase().includes(q) ||
        r.dbo.toLowerCase().includes(q)      || r.table.toLowerCase().includes(q) ||
        r.field.toLowerCase().includes(q),
      );
    }

    for (const col of COLS) {
      const f = filters[col.key];
      if (f) rows = rows.filter((r) => matchesFilter(r[col.key], f));
    }

    rows = [...rows].sort((a, b) => {
      const order = [sortCol, ...SORT_CHAIN.filter((c) => c !== sortCol)];
      for (const c of order) {
        const d = fastCmp(a[c], b[c]);
        if (d !== 0) return sortDir === 'asc' ? d : -d;
      }
      return 0;
    });

    return rows;
  }, [allRows, search, filters, sortCol, sortDir]);

  // ── Virtual scroll window ──────────────────────────────────────────────────
  const totalHeight  = visibleRows.length * ROW_HEIGHT;
  const startIndex   = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const endIndex     = Math.min(visibleRows.length, Math.ceil((scrollTop + listHeight) / ROW_HEIGHT) + OVERSCAN);
  const renderedRows = visibleRows.slice(startIndex, endIndex);
  const offsetY      = startIndex * ROW_HEIGHT;

  // ── Callbacks ─────────────────────────────────────────────────────────────

  const handleSort = useCallback((col: SortCol) => {
    setSortCol((prev) => {
      if (prev === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      else setSortDir('asc');
      return col;
    });
  }, []);

  const setFilter = useCallback((col: SortCol, value: string) => {
    setFilterInputs((prev) => ({ ...prev, [col]: value }));
  }, []);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop((e.currentTarget as HTMLDivElement).scrollTop);
  }, []);

  const hasActiveFilter = searchInput || Object.values(filterInputs).some(Boolean);

  const handleCopy = useCallback(() => {
    const header = 'Document\tModule\tDbo\tTable\tField';
    const body = visibleRows.map((r) => `${r.document}\t${r.module}\t${r.dbo}\t${r.table}\t${r.field}`).join('\n');
    navigator.clipboard.writeText(`${header}\n${body}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }, [visibleRows]);

  const handleDownload = useCallback(() => {
    const header = 'Document,Module,Dbo,Table,Field';
    const body = visibleRows
      .map((r) => `"${r.document}","${r.module}","${r.dbo}","${r.table}","${r.field}"`)
      .join('\n');
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'schema-items.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [visibleRows]);

  if (!open) return null;

  const outerStyle: React.CSSProperties = embedded
    ? {
        // Fill the modal container; no fixed positioning.
        position: 'relative', width: '100%', height: '100%',
        background: 'white',
        display: 'flex', flexDirection: 'column',
      }
    : {
        position: 'absolute', top: 82, right: 0, bottom: 28, width: 820,
        background: 'white', borderLeft: '1px solid #e5e7eb',
        display: 'flex', flexDirection: 'column', zIndex: 30,
        boxShadow: '-4px 0 16px rgba(0,0,0,0.08)',
      };

  return (
    <div style={outerStyle}>

      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', padding: '8px 14px',
        borderBottom: '1px solid #e5e7eb', gap: 6, flexShrink: 0,
      }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: '#111827', flex: 1 }}>Schema Items</span>

        <button
          onClick={() => setShowFields((v) => !v)}
          title={showFields ? 'Hide field rows' : 'Expand to show fields'}
          style={{
            padding: '3px 8px', fontSize: 10, fontWeight: 500, cursor: 'pointer',
            border: '1px solid #d1d5db', borderRadius: 6,
            background: showFields ? '#ede9fe' : 'white',
            color: showFields ? '#6d28d9' : '#6b7280',
          }}
        >
          {showFields ? '▾ Fields on' : '▸ Fields'}
        </button>

        <button
          onClick={handleCopy}
          title="Copy visible rows as TSV"
          style={{
            padding: '3px 8px', fontSize: 10, fontWeight: 500, cursor: 'pointer',
            border: '1px solid #d1d5db', borderRadius: 6,
            background: copied ? '#d1fae5' : 'white',
            color: copied ? '#065f46' : '#374151',
            transition: 'background 0.2s',
          }}
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>

        <button
          onClick={handleDownload}
          title="Download visible rows as CSV"
          style={{
            padding: '3px 8px', fontSize: 10, fontWeight: 500, cursor: 'pointer',
            border: '1px solid #d1d5db', borderRadius: 6, background: 'white', color: '#374151',
          }}
        >
          ↓ CSV
        </button>

        {!embedded && (
          <button
            onClick={onClose}
            title="Close panel"
            style={{
              padding: '3px 7px', fontSize: 15, cursor: 'pointer', lineHeight: 1,
              border: '1px solid #e5e7eb', borderRadius: 6, background: 'white', color: '#6b7280',
            }}
          >×</button>
        )}
      </div>

      {/* ── Global search ── */}
      <div style={{ padding: '6px 14px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
        <div style={{ position: 'relative' }}>
          <span style={{
            position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
            color: '#9ca3af', fontSize: 12, pointerEvents: 'none',
          }}>🔍</span>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search all columns…"
            style={{
              width: '100%', paddingLeft: 26, paddingRight: searchInput ? 26 : 8,
              paddingTop: 5, paddingBottom: 5,
              border: '1px solid #d1d5db', borderRadius: 6, fontSize: 11,
              outline: 'none', boxSizing: 'border-box', color: '#111827',
            }}
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              style={{
                position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14,
              }}
            >×</button>
          )}
        </div>
      </div>

      {/* ── Column headers + per-column filters ── */}
      <div style={{ flexShrink: 0, borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', background: '#f9fafb' }}>
          {showCheckboxes && (
            <div style={{
              width: CHECKBOX_COL_WIDTH, flexShrink: 0, padding: '5px 0',
              fontSize: 10, fontWeight: 600, color: '#6b7280',
              textAlign: 'center', borderRight: '1px solid #f3f4f6',
            }} title="Select rows to add as starter cards in SQL Investigation">
              ☐
            </div>
          )}
          {COLS.map((col) => (
            <div key={col.key} onClick={() => handleSort(col.key)} style={HDR_STYLES[col.key]}>
              {col.label}
              <SortIcon col={col.key} sortCol={sortCol} sortDir={sortDir} />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', borderTop: '1px solid #f3f4f6' }}>
          {showCheckboxes && (
            <div style={{ width: CHECKBOX_COL_WIDTH, flexShrink: 0 }} />
          )}
          {COLS.map((col) => (
            <div key={col.key} style={FILTER_STYLES[col.key]}>
              <input
                value={filterInputs[col.key]}
                onChange={(e) => setFilter(col.key, e.target.value)}
                placeholder="… or =exact"
                title="Plain text matches anywhere (contains). Prefix with = for an exact match, e.g. =booking"
                style={{
                  width: '100%', padding: '2px 4px', fontSize: 10,
                  border: '1px solid #e5e7eb', borderRadius: 3, outline: 'none',
                  boxSizing: 'border-box', color: '#374151',
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* ── Virtual scroll list ── */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        style={{ flex: 1, overflowY: 'auto', position: 'relative' }}
      >
        {visibleRows.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
            No items match the current filters.
          </div>
        ) : (
          // Outer div sets the full scrollable height so the scrollbar is sized correctly
          <div style={{ height: totalHeight, position: 'relative' }}>
            {/* Inner div is shifted to the correct offset — only visible rows are in the DOM */}
            <div style={{ position: 'absolute', top: offsetY, left: 0, right: 0 }}>
              {renderedRows.map((row, i) => {
                const actualIndex = startIndex + i;
                const isSelected = showCheckboxes && !!row.fqn && (selectedFqns?.has(row.fqn) ?? false);
                return (
                  <div
                    key={`${row.document}|${row.module}|${row.table}|${row.field}|${actualIndex}`}
                    style={{
                      height: ROW_HEIGHT,
                      display: 'flex', alignItems: 'stretch',
                      borderBottom: '1px solid #f3f4f6',
                      background: isSelected
                        ? '#eef2ff'
                        : actualIndex % 2 === 0 ? 'white' : '#fafafa',
                    }}
                  >
                    {showCheckboxes && (
                      <div
                        onClick={() => row.fqn && onToggleSelection?.(row.fqn)}
                        title={row.fqn ? `Click to ${isSelected ? 'deselect' : 'select'} ${row.fqn}` : 'No table on this row'}
                        style={{
                          width: CHECKBOX_COL_WIDTH, flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: row.fqn ? 'pointer' : 'default',
                          borderRight: '1px solid #f3f4f6',
                          color: isSelected ? '#4338ca' : '#9ca3af',
                          fontSize: 13,
                        }}
                      >
                        {row.fqn ? (isSelected ? '☑' : '☐') : ''}
                      </div>
                    )}
                    <div title={row.document} style={CELL_STYLES.document}>{row.document}</div>
                    <div title={row.module}   style={CELL_STYLES.module}  >{row.module}</div>
                    <div title={row.dbo}      style={CELL_STYLES.dbo}     >{row.dbo}</div>
                    <div
                      title={row.fqn ? `${row.fqn} — click for fields, types & FKs` : row.table}
                      style={row.fqn ? { ...CELL_STYLES.table, cursor: 'pointer', textDecoration: 'underline' } : CELL_STYLES.table}
                      onClick={() => row.fqn && openDetail(row.fqn)}
                    >{row.table}</div>
                    <div
                      title={row.fqn && row.field ? `Click to see ${row.field}'s type & description` : row.field}
                      style={row.fqn && row.field ? { ...CELL_STYLES.field, cursor: 'pointer' } : CELL_STYLES.field}
                      onClick={() => row.fqn && row.field && openDetail(row.fqn, row.field)}
                    >{row.field}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <div style={{
        padding: '5px 14px', borderTop: '1px solid #e5e7eb', flexShrink: 0,
        fontSize: 10, color: '#6b7280', background: '#f9fafb',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span>
          {visibleRows.length.toLocaleString()} row{visibleRows.length !== 1 ? 's' : ''}
          {!showFields && (
            <span style={{ color: '#9ca3af', marginLeft: 6 }}>
              — toggle ▸ Fields to include columns
            </span>
          )}
        </span>
        {hasActiveFilter && (
          <button
            onClick={() => { setSearchInput(''); setFilterInputs({ ...EMPTY_FILTERS }); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#6366f1', fontSize: 10, fontWeight: 500, padding: 0,
            }}
          >Clear all</button>
        )}
      </div>

      {/* ── Caller-supplied action bar (used by SchemaBrowserModal) ── */}
      {footerSlot && (
        <div style={{ flexShrink: 0, borderTop: '1px solid #e5e7eb' }}>
          {footerSlot}
        </div>
      )}

      {/* ── Table detail drawer (overlays the whole panel) ── */}
      {detail && graphData && (
        <TableDetailDrawer
          node={detail.node}
          graphData={graphData}
          initialFieldFilter={detail.fieldFilter}
          onClose={() => setDetail(null)}
          onJumpToTable={jumpToTable}
        />
      )}
    </div>
  );
}

// VPN_CLIENT_PREFIXES is exported so IssueAnalysisPanel can reference the same list
export { VPN_CLIENT_PREFIXES };
